/**
 * A SKU can be spread across several vendors' offers, each holding its own
 * stock. Ordering more than one vendor has means drawing from several of them,
 * so a requested quantity has to be split before it can be added to the cart.
 */
export type AllocatableOfferT = {
  id: string;
  stock: number;
};

export type OfferAllocationT = {
  offerId: string;
  quantity: number;
};

/**
 * Splits a quantity across offers, filling each in the order given before
 * moving to the next — the order MsCatalog2 returns them in, which is the order
 * it wants them consumed (cheapest/best-scoring first).
 *
 * Asking for 13 against stocks of 5, 6, 1 and 22 draws 5, 6, 1 and 1.
 *
 * Offers with no stock are skipped. When the offers cannot cover the quantity
 * the split stops short rather than over-committing, so callers should compare
 * `allocatedQuantity` against what they asked for.
 */
export const allocateQuantity = (
  offers: AllocatableOfferT[],
  quantity: number,
): OfferAllocationT[] => {
  let remaining = Math.max(0, Math.floor(quantity));

  if (remaining === 0) {
    return [];
  }

  const allocations: OfferAllocationT[] = [];

  for (const offer of offers) {
    if (remaining === 0) {
      break;
    }

    const available = Math.max(0, Math.floor(offer.stock ?? 0));
    const take = Math.min(remaining, available);

    if (take > 0) {
      allocations.push({ offerId: offer.id, quantity: take });
      remaining -= take;
    }
  }

  return allocations;
};

/** How many units a split actually covers. */
export const allocatedQuantity = (allocations: OfferAllocationT[]): number =>
  allocations.reduce((total, { quantity }) => total + quantity, 0);
